import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, message, Space, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { debounce } from 'lodash';

import { SendIcon } from '~/img';
import { Beneficiary, BeneficiaryAccount, Consent, SendMoneyState, Transaction } from '~/types';
import c from '~/utils/constants';
import ToAccount from './ToAccount';
import {
  createPayoutConsent,
  makePayoutTransfer,
  PayoutConsentData,
  updatePayoutConsent,
} from '~/api/payouts';
import SuccessScreen from './SuccessScreen';
import { Drawer } from '~/components';
import TransactionProcessing from '../../../components/transactionProcessing/TransactionProcessing';
import FromAccount from '~/components/sendMoney/FromAccount';
import { getAmountText } from '~/components/fields/AmountField';
import SendAmount from '~/components/sendMoney/SendAmount';
// import Comment from './Comment';

interface Props {
  beneficiaryAccount: BeneficiaryAccount;
  beneficiary: Beneficiary;
  className?: string;
  disabled?: boolean;
}

const SendMoney: FC<Props> = (props) => {
  const { disabled, beneficiaryAccount, beneficiary, className } = props;
  const [t] = useTranslation('payouts');
  // const dt = (key: string): string => t(`translation:${key}`);

  // const nominatedAccount = beneficiary.nominatedAccounts[index];
  const [mainState, setMainState] = useState<SendMoneyState>({
    ...initialState,
    service: getService(beneficiaryAccount),
  });
  const [transfer, setTransfer] = useState<Transaction | undefined>();
  const [form] = Form.useForm();
  const [consent, setConsent] = useState<Consent>({
    id: '',
  } as any);
  const [loading, setLoading] = useState(false);

  const handleTransfer = async () => {
    setLoading(true);
    try {
      const res = await makePayoutTransfer(consent.id);
      setTransfer(res.data);
    } catch (error: any) {
      void message.error(error.response?.data?.message);
    }
    setLoading(false);
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setMainState({
      ...initialState,
      service: getService(beneficiaryAccount),
    });
    setTransfer(undefined);
    setOpen(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleConsent = useCallback(
    debounce(async (data: SendMoneyState) => {
      const payload = {
        senderAccountId: data?.senderAccountId,
        service: data?.service,
        amount: {
          value: data?.amount?.value,
          currency: data?.amount?.currency,
        },
      } as PayoutConsentData;
      const consentId = data?.consentId;
      try {
        const res = consentId
          ? await updatePayoutConsent(consentId, payload)
          : await createPayoutConsent(payload);
        setConsent(res.data);
        setMainState((prevState: any) => ({
          ...prevState,
          consentId: res?.data?.id,
        }));
      } catch (error: any) {
        if (error.response?.data?.message) {
          void message.error(error.response?.data?.message);
        }
        setConsent(({ id }) => ({ id } as any));
        setMainState((prevState: any) => ({
          ...prevState,
          consentId: '',
        }));
      }
      setLoading(false);
    }, 1000),
    []
  );
  useEffect(() => {
    const { amount, senderAccountId } = mainState;
    if ((amount?.value || 0) > 0 && amount?.currency && senderAccountId) {
      setLoading(true);
      void handleConsent(mainState);
    } else {
      setConsent(({ id }) => ({ id } as any));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainState.amount, mainState.senderAccountId]);
  return (
    <>
      <Button type="primary" onClick={handleOpen} disabled={disabled}>
        <SendIcon />
        {t('sendMoney')}
      </Button>
      <Drawer
        open={open}
        onClose={handleClose}
        title={t('sendMoney')}
        placement="bottom"
        className={`${className} SendMoney`}
      >
        <div className="SendMoney-ContentWrapper">
          {transfer ? (
            <TransactionProcessing
              transactionId={transfer.id}
              onComplete={setTransfer}
              completeComponent={<SuccessScreen transfer={transfer} onClose={handleClose} />}
            />
          ) : (
            <Space direction="vertical" size={24} className="SendMoney-Content">
              <FromAccount mainState={mainState} setMainState={setMainState} />
              <ToAccount beneficiary={beneficiary} beneficiaryAccount={beneficiaryAccount} />
              {/* <Comment mainState={mainState} setMainState={setMainState} /> */}
              <SendAmount
                consent={consent}
                loading={loading}
                setMainState={setMainState}
                form={form}
                disabled={!mainState.senderAccountId}
              />
              {consent.totalAmount && !loading && (
                <Button
                  type="primary"
                  className="SendAmount-SendButton"
                  block
                  onClick={handleTransfer}
                  loading={loading}
                >
                  {t('transfer', { amount: getAmountText(consent.totalAmount, true) })}
                </Button>
              )}
            </Space>
          )}
        </div>
      </Drawer>
    </>
  );
};

const initialState = {
  senderAccountId: undefined,
  service: undefined,
  amount: {
    value: undefined,
    currency: undefined,
  },
};

const getService = (nominatedAccount: BeneficiaryAccount): SendMoneyState['service'] => {
  const naParams = nominatedAccount.parameters;
  const parameters: Record<string, string | number> = {};
  Object.keys(naParams).forEach((key) => {
    parameters[key] = naParams[key].value;
  });
  return {
    id: nominatedAccount.serviceId,
    parameters,
  };
};

export default styled(SendMoney)`
  .ant-drawer-content,
  .ant-drawer-header {
    background: ${c.layoutBodyBackground};
  }
  .SendMoney-ContentWrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    .SendMoney-Content {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 760px;
      flex-grow: 1;
      .ant-space-item {
        width: 100%;
      }
    }
  }
  .SendMoney-TotalAmount {
    font-size: 18px;
    color: ${c.primaryColor};
  }
`;
